import React, { useState, useEffect } from "react";
import styles from "./learningZoneContent.module.css";
import { FaPeopleGroup } from "react-icons/fa6";
import { MdUpdate } from "react-icons/md";
import LearningTool from "../learningTool/LearningTool";
import axios from "axios";

const LearningZoneContent = ({
  currUsername,
  lzCreatorUsername,
  isCreatorOrAdmin,
  learningZoneData,
  removeToolFromList,
  maxUnitsPerTool,
}) => {
  const apiUrl = process.env.REACT_APP_URL;

  const [learningTools, setLearningTools] = useState([]);
  const [minToolId, setMinToolId] = useState(null);
  const [areMoreToolsToFetch, setAreMoreToolsToFetch] = useState(false);

  const BASE_URL = `${apiUrl}/`;

  useEffect(() => {
    if (learningZoneData && learningZoneData.learningToolsWithUnits) {
      const newTools = learningZoneData.learningToolsWithUnits;

      if (newTools.length > 0) {
        if (newTools.length >= 5) {
          setAreMoreToolsToFetch(true);
        }

        setLearningTools((prevTools) => {
          // Filter out duplicates and merge the arrays
          const updatedTools = [
            ...newTools.filter(
              (newTool) =>
                !prevTools.some(
                  (existingTool) =>
                    existingTool.learning_tool_id === newTool.learning_tool_id
                )
            ),
            ...prevTools,
          ];
          return updatedTools;
        });

        const minId = Math.min(
          ...newTools.map((tool) => tool.learning_tool_id)
        );
        setMinToolId(minId);
      }
    }
  }, [learningZoneData]);

  // Update local state when a tool is removed
  const handleRemoveTool = (toolId) => {
    setLearningTools((prevTools) =>
      prevTools.filter((tool) => tool.learning_tool_id !== toolId)
    );
    removeToolFromList(toolId); // Call parent to update parent state as well
  };

  /* Handle the View More button click - get more learning tool from the database an display them */
  const handleViewMoreBtnClick = async () => {
    try {
      const learningZoneId = learningZoneData.basicLZInfo[0].learning_zone_id;
      const newMaxToolId = minToolId - 1;

      const lzRes = await axios.get(
        `${BASE_URL}learning_tools/${learningZoneId}`,
        {
          params: { max_tool_id: newMaxToolId, qty: 5 },
          withCredentials: true,
        }
      );

      const fetchedTools = lzRes.data.result;

      if (fetchedTools && fetchedTools.length > 0) {
        setLearningTools((prevTools) => {
          const updatedTools = [...prevTools];

          fetchedTools.forEach((newTool) => {
            // Ensure no duplicates in the updated tools array
            if (
              !updatedTools.some(
                (tool) => tool.learning_tool_id === newTool.learning_tool_id
              )
            ) {
              updatedTools.push(newTool);
            }
          });

          return updatedTools;
        });

        // Update the minToolId only after state update
        const newMinToolId = Math.min(
          ...fetchedTools.map((tool) => tool.learning_tool_id)
        );
        setMinToolId(newMinToolId);

        if (fetchedTools.length < 5) {
          setAreMoreToolsToFetch(false);
        }
      } else {
        setAreMoreToolsToFetch(false);
      }
    } catch (error) {
      console.error("Failed to fetch more learning tools:", error);
    }
  };

  /* Include only learning tools with at least one learning unit. */
  const learningToolsData = learningTools
    .filter(
      (learningTool) => learningTool.units && learningTool.units.length > 0
    )
    .map((learningTool, idx) => {
      return (
        <div key={learningTool.learning_tool_id}>
          <LearningTool
            tool={learningTool}
            currUsername={currUsername}
            lzCreatorUsername={lzCreatorUsername}
            isCreatorOrAdmin={isCreatorOrAdmin}
            removeToolFromList={handleRemoveTool} // Use the local handler
            maxUnitsPerTool={maxUnitsPerTool}
          />
        </div>
      );
    });

  return (
    <div className={styles.learningZoneContent}>
      <section className={styles.learningZoneHeader}>
        <h1>{learningZoneData.basicLZInfo[0].learning_zone_name}</h1>
        <div className={styles.creatorInfo}>
          <figure>
            <img
              src={`${BASE_URL}${learningZoneData.basicLZInfo[0].creator_img_link.replace(
                /\\/g,
                "/"
              )}`}
              alt={learningZoneData.basicLZInfo[0].creator_username}
            />
            <figcaption>
              By {learningZoneData.basicLZInfo[0].creator_username}
            </figcaption>
          </figure>
        </div>
        <h3>Learning {learningZoneData.basicLZInfo[0].language}</h3>
        <div className={styles.extraInfo}>
          <p>
            <span className={styles.membersIcon}>
              <FaPeopleGroup />
            </span>
            <span>{learningZoneData.lZMembersNum}</span> members
          </p>
          <p>
            <span className={styles.updateIcon}>
              <MdUpdate />
            </span>
            Updated on{" "}
            <span>{learningZoneData.basicLZInfo[0].updated_date}</span>
          </p>
        </div>
      </section>
      <div className={styles.learningToolsContainer}>{learningToolsData}</div>
      {areMoreToolsToFetch && (
        <div className={styles.viewMoreBtn}>
          <button onClick={handleViewMoreBtnClick}>View More</button>
        </div>
      )}
    </div>
  );
};

export default LearningZoneContent;
